import JsPDF from "jspdf";

const APPRECIATION_LABELS = {
    fr: ["Mediocre", "Passable", "Assez Bien", "Bien", "Très bien", "Excellent"],
    en: ["Not enough", "Average", "Fairly Good", "Good", "Very Good", "Excellent"],
};

/**
 * Maps a value (0–20) to an appreciation label using fixed bands.
 * @param {number} value - Number between 0 and 20
 * @param {'fr'|'en'} [language='fr'] - Label language (French default, English optional)
 * @returns {string}
 */
function valueToAppreciation(value, language = "fr", overrideLabels = null) {
    const labels = overrideLabels?.[language] ?? APPRECIATION_LABELS[language] ?? APPRECIATION_LABELS.fr;
    // verify if value is a number and between 0 and 20
    if (isNaN(value) || value === null || value === undefined) return "--";
    value = Number(value);
    if (value < 0 || value > 20) return "--";
    if (value < 10) return labels[0];
    if (value < 12) return labels[1];
    if (value < 14) return labels[2];
    if (value < 16) return labels[3];
    if (value < 19) return labels[4];
    return labels[5];
}

/**
 * Returns an object with appreciation labels for mark, min and max.
 * All use the same fixed scale: 0–10 Passable, 10–12.5 Assez Bien,
 * 12.5–15 Bien, 15–17.5 Très bien, 17.5–20 Excellent.
 * @param {number} mark - Value between 0 and 20
 * @param {number} min - Minimum bound (0–20)
 * @param {number} max - Maximum bound (0–20)
 * @param {'fr'|'en'} [options] - Optional. If string: language ('fr'|'en'). If object: { language: 'fr'|'en' }. Omit for French.
 * @returns {{ appreciation: string, min: string, max: string }}
 */
function appreciationMark(mark, min, max, options, overrideLabels = null) {
    const language = typeof options === "string" ? options : (options?.language ?? "fr");
    return {
        appreciation: valueToAppreciation(mark, language, overrideLabels),
        min: valueToAppreciation(min, language, overrideLabels),
        max: valueToAppreciation(max, language, overrideLabels),
    };
}

const colorParentheses = {
    redpink: "#ce8987",
    grayBlue: "#82a8cf",
    white: "#ffffff",
    gray: "#d6d6d6",
    black: "#3D3428",
    blue: "#3e93ef",
    red: "#e65c5c",
}

const autoTableStyles = {
    moduleName: {
        font: "Amiri",
        fontSize: 14,
        halign: "center",
        valign: "middle",
        fontStyle: "Bold",
        fillColor: colorParentheses.redpink,
        textColor: colorParentheses.black,
        lineColor: colorParentheses.grayBlue,
        lineWidth: 0.7,
    },
    teacherName: {
        font: "Amiri",
        fontSize: 14,
        halign: "center",
        valign: "middle",
        fontStyle: "Bold",
        fillColor: colorParentheses.white,
        textColor: colorParentheses.black,
        lineColor: colorParentheses.white,
        lineWidth: 0,
    },
    subjectName: {
        font: "Amiri",
        fontSize: 12,
        halign: "center",
        valign: "middle",
        fontStyle: "Normal",
        fillColor: colorParentheses.redpink,
        textColor: colorParentheses.black,
        lineColor: colorParentheses.grayBlue,
        lineWidth: 0.7,
    },
    secondHeader: {
        font: "Amiri",
        fontSize: 12,
        halign: "center",
        valign: "middle",
        fontStyle: "Bold",
        fillColor: colorParentheses.gray,
        textColor: colorParentheses.black,
        lineColor: colorParentheses.grayBlue,
        lineWidth: 0.7,
    },
    body: {
        font: "Amiri",
        fontSize: 12,
        halign: "center",
        valign: "middle",
        fontStyle: "normal",
        fillColor: colorParentheses.white,
        textColor: colorParentheses.black,
        lineColor: colorParentheses.grayBlue,
        lineWidth: 0.7,
    }
}


const drawBulletin = function (doc, bulletinData, teachersAccess) {
    const pdf_width = doc.internal.pageSize.getWidth();
    const pdf_height = doc.internal.pageSize.getHeight();
    let X = 10;
    let Y = 10;

    // name and last name
    const studentFullName = bulletinData.fullNameFr || bulletinData.fullName;
    doc.setFont("Amiri", "Bold");
    doc.setTextColor(67, 62, 63);
    doc.setFontSize(12);
    let text = "Nom et Prénom :";
    let width = doc.getTextWidth(text);
    doc.text(text, X, Y);

    doc.setTextColor(255, 100, 100);// slightly clear red
    doc.setFontSize(20);
    text = studentFullName;
    width = doc.getTextWidth(text);
    doc.text(text, pdf_width / 2 - width / 2, Y);

    Y += 10

    // classroom
    const classroomMapper = (level) => {
        switch (level) {
            case 1:
                return "1 ère année";
            case 2:
                return "2 ème année";
            case 3:
                return "3 ème année";
            case 4:
                return "4 ème année";
            case 5:
                return "5 ème année";
            case 6:
                return "6 ème année";
        }
    }
    const classroom = classroomMapper(bulletinData.classroomLevel);
    doc.setTextColor(67, 62, 63);
    doc.setFontSize(14);
    text = "Classe :";
    width = doc.getTextWidth(text);
    doc.text(text, X, Y);
    text = classroom;
    width = doc.getTextWidth(text);
    doc.text(text, 55, Y);

    Y += 5

    // module arrays
    for (const module of bulletinData.modules) {
        const autoTableArray = []
        const teacher = teachersAccess.find(teacher => teacher.appAccess?.some(appAccess => appAccess.module === module._id && appAccess.canAppModule));
        // 1st row: module name and teacher name
        autoTableArray.push(
            [
                {
                    content: module.name,
                    styles: autoTableStyles.moduleName,
                    colSpan: 2,
                },
                {
                    content: teacher?.fullName ?? "----",
                    styles: autoTableStyles.teacherName,
                    colSpan: 3,
                }
            ]
        )

        // 2nd row: Body headers
        autoTableArray.push(
            [
                {
                    content: "Matière",
                    styles: autoTableStyles.secondHeader,
                },
                {
                    content: "Apprèciation",
                    styles: autoTableStyles.secondHeader,
                    colSpan: 2,
                },
                {
                    content: "Max",
                    styles: autoTableStyles.secondHeader,
                },
                {
                    content: "Min",
                    styles: autoTableStyles.secondHeader,
                },
            ]
        )

        // 3rd row: Body rows
        let lang = "fr";
        if (module.name.toLowerCase().includes("english") || module.namePersonalized.toLowerCase().includes("english")) lang = "en";
        module.subjects.forEach(subject => {
            const textMarks = appreciationMark(subject.mark, subject.min, subject.max, lang);
            autoTableArray.push(
                [
                    {
                        content: subject.name,
                        styles: autoTableStyles.subjectName,
                    },
                    {
                        content: textMarks.appreciation,
                        styles: autoTableStyles.body,
                        colSpan: 2,
                    },
                    {
                        content: textMarks.max,
                        styles: autoTableStyles.body,
                    },
                    {
                        content: textMarks.min,
                        styles: autoTableStyles.body,
                    },
                ]
            )
        })

        doc.autoTable({
            theme: "grid",
            body: autoTableArray,
            startY: Y,
            columnStyles: {
                0: { cellWidth: 25 },
                1: { cellWidth: 25 },
                2: { cellWidth: 25 },
                3: { cellWidth: 25 },
                4: { cellWidth: 25 },
            },
        });

        // module notes
        const moduleNotesY = Y + 5;
        doc.setFillColor(colorParentheses.white);
        doc.setLineWidth(0.4);


        doc.setDrawColor(colorParentheses.blue);
        doc.rect(142, moduleNotesY + 4, 66, 17, "FD");

        doc.setDrawColor(colorParentheses.red);
        doc.rect(145, moduleNotesY, 60, 8, "FD");

        doc.setFont("Amiri", "Bold");
        doc.setFontSize(14);
        doc.setTextColor(colorParentheses.black);
        text = "Note de l'enseignant(e)"
        doc.text(text, 155, moduleNotesY + 6);

        doc.setFont("Amiri", "Bold");
        doc.setFontSize(12);
        doc.setTextColor(colorParentheses.black);
        // First, check if module.appreciation is not empty after trimming whitespace
        let cleanedAppreciation = "";
        if (module.appreciation && typeof module.appreciation === "string" && module.appreciation.trim() !== "") {
            // Normalize control whitespace and keep readable spacing between words
            cleanedAppreciation = module.appreciation
                .replace(/[\n\r\t]+/g, " ") // turn newlines/tabs into regular spaces
                .replace(
                    /[^\u0621-\u064A\u0660-\u0669A-Za-z0-9 .,;!?()\[\]{}=+\-_'"/\\:،؛؟ــ«»<>%$@#*^`~§|]/g, ""
                    // keep Arabic letters, Arabic-Indic digits, Latin alphanumerics, spaces, and common punctuation
                )
                .replace(/ {2,}/g, " ")
                .trim();
        }
        const texts = doc.splitTextToSize(cleanedAppreciation || "", 60);
        let splitY = moduleNotesY + 12;
        for (const split of texts) {
            doc.text(split, 145, splitY);
            splitY += 4;
        }





        Y = doc.previousAutoTable.finalY + 15;
    }


}

const printBulletin = function (bulletinData, teachersAccess) {
    const doc = new JsPDF();
    drawBulletin(doc, bulletinData, teachersAccess);
    doc.save(`Bulletin_${bulletinData.fullNameFr || bulletinData.fullName}.pdf`);

}

const printAllBulletin = function (bulletinsData, teachersAccess) {
    const doc = new JsPDF();
    let className = "__"
    for (let i = 0; i < bulletinsData.length; i++) {
        const bulletinData = bulletinsData[i];
        if (i === 0) {
            className = bulletinData.classroomName;
        }
        if (i > 0) {
            doc.addPage();
        }
        drawBulletin(doc, bulletinData, teachersAccess);
    }

    doc.save(`Bulletins_${className}.pdf`);
}
export default {
    appreciationMark,
    printBulletin,
    printAllBulletin,
};
